/**
 * Sign-out coordination shared by the auth adapters, the API middleware, and
 * the boot sequence (#44).
 *
 * Sign-out must be ORDERED: no request may leave the application once
 * sign-out begins, other open tabs must drop to a signed-out state, and a
 * provider-logout failure must not let the boot sequence silently sign the
 * same account straight back in. Three cooperating pieces provide that:
 *
 * - an in-memory gate (`beginSignOut` / `isSigningOut`) that the API
 *   middleware consults before sending anything at all;
 * - a BroadcastChannel that tells the other tabs of this origin to stop. The
 *   token cache is per-tab session storage, so without the broadcast a second
 *   tab would keep an authenticated, mutation-capable UI after the first one
 *   signed out;
 * - a session-storage marker for the one path where sign-out could not reach
 *   the provider. The boot sequence reads it and offers an explicit sign-in
 *   instead of auto-starting one, because an auto-started sign-in against a
 *   surviving Entra SSO session signs the old account back in without ever
 *   showing a prompt — the exact outcome a deliberate sign-out exists to
 *   prevent.
 */

const CHANNEL_NAME = "oneliv.editor.signout";

/**
 * Session storage, not local storage: the marker describes THIS tab's failed
 * provider sign-out, and a fresh browser must start clean (#44's "a fresh
 * browser cannot recover the previous session").
 */
const LOCAL_SIGN_OUT_MARKER_KEY = "oneliv.editor.signedOutLocally";

type SignOutMessage = { type: "signed-out" };

let signingOut = false;
let channel: BroadcastChannel | null = null;
const remoteListeners = new Set<() => void>();

/**
 * Lazily created so that merely importing this module never opens a channel.
 * `BroadcastChannel` is feature-detected because the unit-test DOM does not
 * implement it; without it the gate still works, only cross-tab propagation
 * is lost — which is also the real-browser behaviour on anything old enough
 * to lack the API.
 */
function ensureChannel(): BroadcastChannel | null {
  if (typeof BroadcastChannel === "undefined") {
    return null;
  }
  if (!channel) {
    channel = new BroadcastChannel(CHANNEL_NAME);
    channel.onmessage = (event: MessageEvent<unknown>) => {
      if ((event.data as SignOutMessage | undefined)?.type !== "signed-out") {
        return;
      }
      // A remote sign-out gates this tab too: its own cache still holds a
      // token, and nothing may be sent with it once the session has ended.
      signingOut = true;
      for (const listener of [...remoteListeners]) {
        listener();
      }
    };
  }
  return channel;
}

/**
 * First step of the ordered sign-out flow. After this call the API
 * middleware refuses every request in this tab, and every other tab of this
 * origin is told to do the same.
 */
export function beginSignOut(): void {
  signingOut = true;
  ensureChannel()?.postMessage({ type: "signed-out" } satisfies SignOutMessage);
}

export function isSigningOut(): boolean {
  return signingOut;
}

/**
 * Notifies when ANOTHER tab signs out. The subscribing tab is expected to
 * clear its own local session and render a signed-out state; this module
 * cannot do that for it because only the adapter knows its cache.
 */
export function subscribeRemoteSignOut(listener: () => void): () => void {
  remoteListeners.add(listener);
  ensureChannel();
  return () => {
    remoteListeners.delete(listener);
  };
}

/** Records that sign-out ended locally without reaching the provider. */
export function markSignedOutLocally(): void {
  window.sessionStorage.setItem(LOCAL_SIGN_OUT_MARKER_KEY, "true");
}

export function hasLocalSignOutMarker(): boolean {
  return window.sessionStorage.getItem(LOCAL_SIGN_OUT_MARKER_KEY) === "true";
}

/** Cleared only by an explicit user action (choosing to sign in again). */
export function clearLocalSignOutMarker(): void {
  window.sessionStorage.removeItem(LOCAL_SIGN_OUT_MARKER_KEY);
}

/** Test seam: returns the module to its pristine state between test cases. */
export function resetSignOutStateForTests(): void {
  signingOut = false;
  remoteListeners.clear();
  channel?.close();
  channel = null;
  window.sessionStorage.removeItem(LOCAL_SIGN_OUT_MARKER_KEY);
}
